import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { notifyResearchAccessChange } from './researchAccessEvents';
import { ZWEI_FAKTOR_PFLICHT, istPflichtFehler } from '../components/account/zweiFaktor';
import { RICHTLINIE_PFLICHT } from '../administration/recht/richtlinieTexte';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,  // IMPORTANT: Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// REMOVED: Request interceptor that added Authorization header from localStorage
// The httpOnly cookie is sent automatically with withCredentials: true

let refreshPromise: Promise<void> | null = null;
// The page (path, query, hash) that a failed refresh already sent to the login page. Further 401s from that page neither
// refresh again (refresh limiter) nor navigate again; the page is about to unload.
let loginNavigationFrom: string | null = null;

// DR-13 UI round 2d: tabs share one cookie jar. When another tab refreshed first, this tab's refresh fails with the already
// rotated refresh token while the jar holds that tab's live session. One profile check decides: a user, or the 403 of the
// two-factor duty or of the usage-policy duty (the session is live, the retried request then gets that 403 and the duty
// page), counts as live. _retry keeps the check out of the interceptor's refresh path, so a 401 here rejects without another
// refresh.
function sessionIsLive(): Promise<boolean> {
  const probe: AxiosRequestConfig & { _retry: boolean } = { _retry: true };
  return apiClient.get('/api/auth/profile', probe)
    .then((response) => !!response.data?.user, (error) => istPflichtFehler(error)
      || (error?.response?.status === 403 && error.response.data?.code === RICHTLINIE_PFLICHT));
}

// Refresh and profile check form one shared promise: requests that failed together cause one refresh and at most one check.
// A refresh without an answer (timeout, network) lost no race; the check would only delay the login page by another timeout.
export function refreshAuthenticatedSession(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = apiClient.post('/api/auth/refresh')
      .then(() => undefined, async (refreshError) => {
        if (refreshError?.response && await sessionIsLive()) return;
        throw refreshError;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function isPublicAuthPage(pathname: string): boolean {
  return pathname === '/login'
    || pathname === '/forgot-password'
    || pathname === '/sso'
    || pathname === '/einladung'
    || pathname === '/registrieren'
    || pathname === '/reset-password'
    || pathname.startsWith('/reset-password/')
    // Paket 08 Schritt 10 (EIN-20, M-17.6): die Bestätigung des E-Mail-Wechsels arbeitet ohne Sitzung.
    || pathname === '/e-mail-bestaetigen';
}

/** GZ-8: eine Antwort 403 mit dem Code der Zwei-Faktor-Pflicht verlangt die Pflichtseite – außer bei einer Profilabfrage
 *  (/api/auth/profile, /api/auth/me, /api/auth/access), deren Fehler AuthContext selbst auswertet. Profil-Nachbau F-17
 *  (PF-787-nach): ebenso der Code der Nutzungsrichtlinie, damit auch eine Anfrage der Verwaltung sofort die Zustimmungsseite
 *  zeigt und nicht erst das nächste checkAuth. */
export function pflichtseiteNoetig(status: number | undefined, code: string | undefined, profilabfrage: boolean): boolean {
  return status === 403 && (code === ZWEI_FAKTOR_PFLICHT || code === RICHTLINIE_PFLICHT) && !profilabfrage;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const accessCode = (error.response?.data as { code?: string } | undefined)?.code;
    if (originalRequest?.url?.includes('/api/ris')) notifyResearchAccessChange(error.response?.status, accessCode);

    const isProfileCheck = originalRequest?.url?.includes('/api/auth/profile')
      || originalRequest?.url?.includes('/api/auth/me')
      || originalRequest?.url?.includes('/api/auth/access');
    // Plan 1a Welle 2, Spur admin (SPEC-ADMIN 12.2, GZ-8; Ä-18 Punkt 2): verlangt die Firma einen zusätzlichen Code, weist der
    // Server jede Anfrage eines Kontos ohne Code mit 403 und diesem Code ab. Die Pflichtseite erscheint, sobald das Profil neu
    // geladen wird (AuthContext hört auf auth:access-changed); so steht ein angemeldetes Mitglied beim nächsten Klick dort
    // und nicht erst nach dem Neuladen der Seite. Die Profilabfragen selbst lösen nichts aus, sonst drehte es sich im Kreis.
    if (pflichtseiteNoetig(error.response?.status, accessCode, !!isProfileCheck)) window.dispatchEvent(new CustomEvent('auth:access-changed'));

    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    const isPublicPasswordEndpoint = originalRequest?.url?.includes('/api/auth/forgot-password')
      || originalRequest?.url?.includes('/api/auth/reset-password');
    // change-password and delete-account confirm a typed password. A 401 that carries
    // an application code answers that password, not the session: refreshing and
    // retrying would cost extra hits on the route and refresh limiters and could log
    // the user out. A 401 without a code (expired access token) still refreshes below.
    const isPasswordConfirmationFailure = !!accessCode
      && (originalRequest?.url?.includes('/api/auth/change-password') || originalRequest?.url?.includes('/api/auth/delete-account'));

    // Public auth screens must be usable without a session. In particular, a
    // reset link mounts AuthProvider and checks /me; that expected 401 must not
    // start refresh/logout navigation away from the reset form.
    const isPublicInvitationEndpoint = originalRequest?.url?.endsWith('/api/administration/invitations/preview')
      || originalRequest?.url?.endsWith('/api/administration/invitations/accept');
    // DR-13 UI-28: a 401 from the SSO entry (inactive partner, bad signature) rejects the link, not a session.
    const isSsoEntry = originalRequest?.url?.endsWith('/sso/entry');
    if (isPublicPasswordEndpoint || isPublicInvitationEndpoint || isSsoEntry || isPasswordConfirmationFailure || (isProfileCheck && isPublicAuthPage(window.location.pathname))) {
      return Promise.reject(error);
    }

    // Don't refresh for auth endpoints themselves. Paket 08 Schritt 6: only the refresh call itself - the own device list
    // under /api/auth/refresh/sessions refreshes an expired access token like every other call.
    const isAuthEndpoint = (originalRequest?.url?.split('?')[0] ?? '').endsWith('/api/auth/refresh')
      || originalRequest?.url?.includes('/api/auth/login');
    if (isAuthEndpoint) {
      // Login errors belong to the form; the original request handles a failed
      // refresh. Clearing auth here could navigate before its return path is saved.
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const returnTarget = window.location.pathname + (window.location.search || '') + (window.location.hash || '');
    if (loginNavigationFrom === returnTarget) return Promise.reject(error);

    try {
      await refreshAuthenticatedSession();
      loginNavigationFrom = null;
      return apiClient(originalRequest);
    } catch (refreshError) {
      window.dispatchEvent(new CustomEvent('auth:logout'));
      // Several requests of one view fail together and share one refresh; only the first one navigates. A second assignment
      // to location.href aborted the first navigation and loaded the login page twice (anmeldung-ui A09).
      if (loginNavigationFrom !== returnTarget && !isPublicAuthPage(window.location.pathname) && !isProfileCheck) {
        loginNavigationFrom = returnTarget;
        window.location.href = '/login?expired=true&returnTo=' + encodeURIComponent(returnTarget);
      }
      return Promise.reject(refreshError);
    }
  }
);

export default apiClient;
